/**
 * PONTO ÚNICO para ler os tokens de sessão.
 *
 * O problema que isto resolve: existem TRÊS convenções de nome a coexistir no
 * localStorage —
 *   'accessToken'                 (login por email e Google — authService)
 *   'access_token'                (usada por alguns clientes de API)
 *   'padelmaster_access_token'    (tokenManager, o único com auto-refresh)
 *
 * Partes do código guardavam numa e procuravam noutra. Quando isso acontecia,
 * o pedido seguia sem autenticação, o servidor respondia 401 e a operação
 * falhava em silêncio. Foi assim que os jogos deixaram de entrar no ranking
 * durante meses sem ninguém notar.
 *
 * Regra a partir de agora: NENHUM ficheiro lê tokens do localStorage
 * diretamente — toda a gente passa por aqui.
 *
 * Nota sobre as ESCRITAS: continuam a espelhar as três convenções de
 * propósito. Deixar de escrever nas antigas expulsaria quem já tem sessão
 * iniciada só numa delas. Como todas as leituras passam a ser feitas aqui, o
 * bug desaparece de imediato; retirar as chaves antigas é limpeza para depois,
 * quando não restarem sessões antigas em circulação.
 */

import { tokenManager } from './tokenManager';

/** Todas as convenções conhecidas, por ordem de preferência. */
const CHAVES_ACCESS = ['padelmaster_access_token', 'accessToken', 'access_token', 'authToken'] as const;
const CHAVES_REFRESH = ['padelmaster_refresh_token', 'refreshToken', 'refresh_token'] as const;

function lerPrimeira(chaves: readonly string[]): string | null {
  for (const chave of chaves) {
    try {
      const valor = localStorage.getItem(chave) || sessionStorage.getItem(chave);
      if (valor) return valor;
    } catch {
      /* storage indisponível (modo privado) — tenta a seguinte */
    }
  }
  return null;
}

/**
 * Token de acesso atual, venha da convenção que vier.
 *
 * Passa primeiro pelo tokenManager porque este valida a validade e dispara a
 * renovação automática quando está perto de expirar. Se ele não devolver nada
 * (ex.: token guardado só numa chave legada que ele não conhece), lê-se
 * diretamente das restantes.
 */
export function lerAccessToken(): string | null {
  try {
    const doGestor = tokenManager.getAccessToken();
    if (doGestor) return doGestor;
  } catch {
    /* nunca deixar o gestor impedir a leitura direta */
  }
  return lerPrimeira(CHAVES_ACCESS);
}

/** Token de renovação atual, venha da convenção que vier. */
export function lerRefreshToken(): string | null {
  try {
    const doGestor = tokenManager.getRefreshToken();
    if (doGestor) return doGestor;
  } catch {
    /* idem */
  }
  return lerPrimeira(CHAVES_REFRESH);
}

/** Há sessão iniciada? */
export function temSessao(): boolean {
  return !!lerAccessToken();
}

/** Cabeçalho de autenticação pronto a usar; vazio se não houver sessão. */
export function cabecalhoAuth(): Record<string, string> {
  const token = lerAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Guarda os tokens em TODAS as convenções.
 *
 * Espelhar é intencional enquanto houver código legado a ler chaves antigas
 * diretamente — ver a nota no topo do ficheiro.
 */
export function guardarTokens(access: string, refresh?: string): void {
  try {
    for (const chave of CHAVES_ACCESS) localStorage.setItem(chave, access);
    if (refresh) {
      for (const chave of CHAVES_REFRESH) localStorage.setItem(chave, refresh);
    }
    localStorage.setItem('loginTimestamp', Date.now().toString());
  } catch (erro) {
    console.warn('[tokens] Não foi possível guardar a sessão:', erro);
  }
}

/** Apaga a sessão de TODAS as convenções — sem deixar nenhuma para trás. */
export function limparTokens(): void {
  tokenManager.clearTokens();
  for (const chave of [...CHAVES_ACCESS, ...CHAVES_REFRESH, 'padelmaster_token_data']) {
    try {
      localStorage.removeItem(chave);
      sessionStorage.removeItem(chave);
    } catch {
      /* ignora */
    }
  }
}
